import React, { useState, useEffect, useCallback, useMemo, useContext, createContext, useRef } from 'react';
import ApiManager from './index';
import { ApiResponse } from './client';
import { User, AuthCredentials, RegisterData, Product, Cart, Category, ListOptions } from './models';

/**
 * Tipo para estados de carga genéricos
 */
interface LoadingState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Tipo para información de paginación
 */
interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
  next?: { page: number; limit: number };
  prev?: { page: number; limit: number };
}

/**
 * Tipo para estados de carga con paginación
 */
interface PaginatedLoadingState<T> extends LoadingState<T> {
  pagination: PaginationInfo | null;
}

/**
 * Tipo para estados de autenticación
 */
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  login: (credentials: AuthCredentials) => Promise<ApiResponse<any>>;
  register: (userData: RegisterData) => Promise<ApiResponse<any>>;
  logout: () => Promise<void>;
  updateProfile: (userData: Partial<User>) => Promise<ApiResponse<User>>;
}

/**
 * Proveedor del contexto de autenticación
 */
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isInitialized: false,
  login: async () => ({ success: false }),
  register: async () => ({ success: false }),
  logout: async () => { },
  updateProfile: async () => ({ success: false })
});

/**
 * Proveedor de autenticación para la aplicación
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Inicializar estado de autenticación al cargar
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Verificar si hay un usuario guardado en localStorage
        if (ApiManager.auth.restoreSession()) {
          const storedUser = ApiManager.auth.getStoredUser();

          if (storedUser) {
            setUser(storedUser);
          } else {
            // Si hay token pero no hay datos de usuario, obtenerlos
            const response = await ApiManager.auth.getProfile();
            if (response.success && response.data) {
              setUser(response.data);
              // Actualizar datos en localStorage
              localStorage.setItem('user', JSON.stringify(response.data));
            }
          }
        }
      } catch (error) {
        console.error('Error al inicializar autenticación:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    initAuth();
  }, []);

  /**
   * Iniciar sesión
   */
  const login = async (credentials: AuthCredentials) => {
    const response = await ApiManager.auth.login(credentials);

    if (response.success && response.data) {
      setUser(response.data.user);

      // Migrar carrito de anónimo a autenticado
      try {
        await ApiManager.cart.migrateCart();
      } catch (error) {
        console.error('Error al migrar carrito:', error);
      }
    }

    return response;
  };

  /**
   * Registrar nuevo usuario
   */
  const register = async (userData: RegisterData) => {
    const response = await ApiManager.auth.register(userData);

    if (response.success && response.data) {
      setUser(response.data.user);

      // Migrar carrito de anónimo a autenticado
      try {
        await ApiManager.cart.migrateCart();
      } catch (error) {
        console.error('Error al migrar carrito:', error);
      }
    }

    return response;
  };

  /**
   * Cerrar sesión
   */
  const logout = async () => {
    await ApiManager.auth.logout();
    setUser(null);
  };

  /**
   * Actualizar perfil
   */
  const updateProfile = async (userData: Partial<User>) => {
    const response = await ApiManager.auth.updateProfile(userData);

    if (response.success && response.data) {
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    }

    return response;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isInitialized,
        login,
        register,
        logout,
        updateProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook para acceder al contexto de autenticación
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};

/**
 * Hook para obtener productos con filtrado y paginación
 */
export const useProducts = (options: ListOptions = {}): PaginatedLoadingState<Product[]> => {
  const [state, setState] = useState<PaginatedLoadingState<Product[]>>({
    data: null,
    loading: true,
    error: null,
    pagination: null,
    refetch: async () => { }
  });

  // Crear una clave estable para las opciones
  const optionsKey = useMemo(() => {
    return JSON.stringify({
      page: options.page || 1,
      limit: options.limit || 25,
      sort: options.sort || 'createdAt',
      order: options.order || 'desc',
      filter: options.filter || {},
      search: options.search || ''
    });
  }, [
    options.page,
    options.limit,
    options.sort,
    options.order,
    options.search,
    options.filter?.category,
    options.filter?.brand,
    options.filter?.priceMin,
    options.filter?.priceMax,
    options.filter?.isActive,
    options.filter?.featured
  ]);

  const fetchProducts = useCallback(async () => {
    const currentOptions = JSON.parse(optionsKey);
    console.log('🔄 [useProducts] Iniciando fetch con opciones:', currentOptions);
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await ApiManager.products.getProducts(currentOptions);

      if (response.success && response.data) {
        setState({
          data: response.data,
          loading: false,
          error: null,
          pagination: response.pagination || null,
          refetch: fetchProducts
        });
      } else {
        setState({
          data: null,
          loading: false,
          error: response.error?.message || 'Error al cargar productos',
          pagination: null,
          refetch: fetchProducts
        });
      }
    } catch (error) {
      setState({
        data: null,
        loading: false,
        error: (error as Error).message || 'Error desconocido',
        pagination: null,
        refetch: fetchProducts
      });
    }
  }, [optionsKey]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return state;
};

/**
 * Hook para obtener un producto específico
 */
export const useProduct = (productId: string | undefined): LoadingState<Product> => {
  const [state, setState] = useState<LoadingState<Product>>({
    data: null,
    loading: true,
    error: null,
    refetch: async () => { }
  });

  const fetchProduct = useCallback(async () => {
    if (!productId) {
      setState({
        data: null,
        loading: false,
        error: 'ID de producto no proporcionado',
        refetch: fetchProduct
      });
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await ApiManager.products.getProduct(productId);

      if (response.success && response.data) {
        setState({
          data: response.data,
          loading: false,
          error: null,
          refetch: fetchProduct
        });
      } else {
        setState({
          data: null,
          loading: false,
          error: response.error?.message || 'Producto no encontrado',
          refetch: fetchProduct
        });
      }
    } catch (error) {
      setState({
        data: null,
        loading: false,
        error: (error as Error).message || 'Error desconocido',
        refetch: fetchProduct
      });
    }
  }, [productId]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  return state;
};

/**
 * Hook para gestionar el carrito de compras
 */
export const useCart = () => {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const skipNextEventRef = useRef(false);

  const fetchCart = useCallback(async (silent: boolean = false) => {
    console.log('🛒 [useCart] fetchCart called, silent:', silent);
    if (!silent) {
      setLoading(true);
    }
    setError(null);

    try {
      console.log('🛒 [useCart] Making API request to getCart...');
      const response = await ApiManager.cart.getCart();
      console.log('🛒 [useCart] getCart response:', response);

      if (response.success && response.data) {
        setCart(response.data);
      } else {
        setError(response.error?.message || 'Error al cargar el carrito');
      }
    } catch (error) {
      setError((error as Error).message || 'Error desconocido');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    console.log('🛒 [useCart] Initial useEffect triggered, calling fetchCart');
    fetchCart();
  }, [fetchCart]);

  // Listen for cart update events from CartContext (silent refresh to avoid loading state)
  // Skip if the event was triggered by this hook's own update
  useEffect(() => {
    const handleCartUpdate = () => {
      if (skipNextEventRef.current) {
        skipNextEventRef.current = false;
        return;
      }
      fetchCart(true);
    };

    window.addEventListener('jarad-cart-updated', handleCartUpdate);
    return () => {
      window.removeEventListener('jarad-cart-updated', handleCartUpdate);
    };
  }, [fetchCart]);

  // Refetch cart when page becomes visible, window gets focus, or restored from bfcache
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchCart(true);
      }
    };

    const handleFocus = () => {
      fetchCart(true);
    };

    // Handle bfcache (back/forward cache) restoration
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // Page was restored from bfcache, refetch cart
        fetchCart(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [fetchCart]);

  const addToCart = useCallback(async (productId: string, quantity: number = 1, variantSku?: string) => {
    try {
      const response = await ApiManager.cart.addToCart(productId, quantity, variantSku);

      if (response.success && response.data) {
        setCart(response.data);
        return true;
      } else {
        setError(response.error?.message || 'Error al añadir al carrito');
        return false;
      }
    } catch (error) {
      setError((error as Error).message || 'Error desconocido');
      return false;
    }
  }, []);

  const updateCartItem = useCallback(async (productId: string, quantity: number, variantSku?: string) => {
    try {
      const response = await ApiManager.cart.updateCartItem(productId, quantity, variantSku);

      if (response.success && response.data) {
        setCart(response.data);
        // Notify CartContext with cart data to avoid another GET
        skipNextEventRef.current = true;
        window.dispatchEvent(new CustomEvent('jarad-cart-updated', { detail: response.data }));
        return true;
      } else {
        setError(response.error?.message || 'Error al actualizar el carrito');
        return false;
      }
    } catch (error) {
      setError((error as Error).message || 'Error desconocido');
      return false;
    }
  }, []);

  const removeFromCart = useCallback(async (productId: string, variantSku?: string) => {
    try {
      const response = await ApiManager.cart.removeFromCart(productId, variantSku);

      if (response.success && response.data) {
        setCart(response.data);
        // Notify CartContext with cart data to avoid another GET
        skipNextEventRef.current = true;
        window.dispatchEvent(new CustomEvent('jarad-cart-updated', { detail: response.data }));
        return true;
      } else {
        setError(response.error?.message || 'Error al eliminar del carrito');
        return false;
      }
    } catch (error) {
      setError((error as Error).message || 'Error desconocido');
      return false;
    }
  }, []);

  const clearCart = useCallback(async () => {
    try {
      const response = await ApiManager.cart.clearCart();

      if (response.success && response.data) {
        setCart(response.data);
        // Notify CartContext with cart data to avoid another GET
        skipNextEventRef.current = true;
        window.dispatchEvent(new CustomEvent('jarad-cart-updated', { detail: response.data }));
        return true;
      } else {
        setError(response.error?.message || 'Error al vaciar el carrito');
        return false;
      }
    } catch (error) {
      setError((error as Error).message || 'Error desconocido');
      return false;
    }
  }, []);

  return {
    cart,
    loading,
    error,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    refetch: fetchCart
  };
};

/**
 * Hook para obtener las categorías
 */
export const useCategories = (): LoadingState<Category[]> => {
  const [state, setState] = useState<LoadingState<Category[]>>({
    data: null,
    loading: true,
    error: null,
    refetch: async () => { }
  });

  const fetchCategories = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await ApiManager.categories.getCategories();

      if (response.success && response.data) {
        setState({
          data: response.data,
          loading: false,
          error: null,
          refetch: fetchCategories
        });
      } else {
        setState({
          data: null,
          loading: false,
          error: response.error?.message || 'Error al cargar categorías',
          refetch: fetchCategories
        });
      }
    } catch (error) {
      setState({
        data: null,
        loading: false,
        error: (error as Error).message || 'Error desconocido',
        refetch: fetchCategories
      });
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return state;
};

/**
 * Opciones de búsqueda de productos con Meilisearch
 */
interface ProductSearchOptions {
  query?: string;
  category?: string;
  brand?: string;
  priceMin?: number;
  priceMax?: number;
  tags?: string[];
  page?: number;
  limit?: number;
  sort?: string;
  isActive?: boolean | 'all';
  stockLessThan?: number;
}

/**
 * Información de facets devuelta por Meilisearch
 */
interface FacetDistribution {
  [facetName: string]: {
    [facetValue: string]: number;
  };
}

/**
 * Estado de carga para búsqueda con facets
 */
interface SearchLoadingState extends PaginatedLoadingState<Product[]> {
  facets: FacetDistribution | null;
  hybridSearch?: boolean;
  semanticRatio?: number;
  processingTimeMs?: number;
}

/**
 * Hook para búsqueda de productos con Meilisearch (con facets y búsqueda híbrida)
 */
export const useProductSearch = (options: ProductSearchOptions = {}): SearchLoadingState => {
  const [state, setState] = useState<SearchLoadingState>({
    data: null,
    loading: true,
    error: null,
    pagination: null,
    facets: null,
    refetch: async () => { }
  });

  // Crear una clave estable para las opciones
  const optionsKey = useMemo(() => {
    return JSON.stringify({
      query: options.query || '',
      category: options.category,
      brand: options.brand,
      priceMin: options.priceMin,
      priceMax: options.priceMax,
      tags: options.tags || [],
      page: options.page || 1,
      limit: options.limit || 25,
      sort: options.sort,
      isActive: options.isActive,
      stockLessThan: options.stockLessThan
    });
  }, [
    options.query,
    options.category,
    options.brand,
    options.priceMin,
    options.priceMax,
    options.tags,
    options.page,
    options.limit,
    options.sort,
    options.isActive,
    options.stockLessThan
  ]);

  const fetchProducts = useCallback(async () => {
    const currentOptions = JSON.parse(optionsKey);
    console.log('🔍 [useProductSearch] Iniciando búsqueda con Meilisearch:', currentOptions);
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      // Construir filtros de Meilisearch
      const filters: any = {
        category: currentOptions.category,
        brand: currentOptions.brand,
        minPrice: currentOptions.priceMin,
        maxPrice: currentOptions.priceMax,
        tags: currentOptions.tags,
        isActive: currentOptions.isActive,
        stockLessThan: currentOptions.stockLessThan
      };

      // Usar búsqueda avanzada de Meilisearch
      const response = await ApiManager.search.advancedSearch(
        currentOptions.query || '',
        filters,
        currentOptions.sort,
        currentOptions.page,
        currentOptions.limit
      );

      if (response.success && response.data) {
        // Construir paginación a partir de los datos de respuesta
        const total = (response as any).total || response.data.length;
        const page = currentOptions.page;
        const limit = currentOptions.limit;
        const pages = Math.ceil(total / limit);

        setState({
          data: response.data,
          loading: false,
          error: null,
          pagination: {
            page,
            limit,
            total,
            pages,
            next: page < pages ? { page: page + 1, limit } : undefined,
            prev: page > 1 ? { page: page - 1, limit } : undefined
          },
          facets: (response as any).facetDistribution || null,
          hybridSearch: (response as any).hybridSearch,
          semanticRatio: (response as any).semanticRatio,
          processingTimeMs: (response as any).processingTimeMs,
          refetch: fetchProducts
        });
      } else {
        setState({
          data: null,
          loading: false,
          error: response.error?.message || 'Error al buscar productos',
          pagination: null,
          facets: null,
          refetch: fetchProducts
        });
      }
    } catch (error) {
      setState({
        data: null,
        loading: false,
        error: (error as Error).message || 'Error desconocido',
        pagination: null,
        facets: null,
        refetch: fetchProducts
      });
    }
  }, [optionsKey]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return state;
};

/**
 * Hook para obtener árbol de categorías
 */
export const useCategoryTree = (): LoadingState<any[]> => {
  const [state, setState] = useState<LoadingState<any[]>>({
    data: null,
    loading: true,
    error: null,
    refetch: async () => { }
  });

  const fetchTree = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await ApiManager.categories.getTree();
      if (response.success && response.data) {
        setState({ data: response.data as any[], loading: false, error: null, refetch: fetchTree });
      } else {
        setState({ data: null, loading: false, error: response.error?.message || 'Error al cargar árbol de categorías', refetch: fetchTree });
      }
    } catch (error) {
      setState({ data: null, loading: false, error: (error as Error).message || 'Error desconocido', refetch: fetchTree });
    }
  }, []);

  useEffect(() => { fetchTree(); }, [fetchTree]);
  return state;
};

/**
 * Hook para obtener breadcrumbs de una categoría
 */
export const useCategoryBreadcrumbs = (categoryId: string | undefined): LoadingState<Array<{ id: string; name: string; slug: string }>> => {
  const [state, setState] = useState<LoadingState<Array<{ id: string; name: string; slug: string }>>>({
    data: null,
    loading: true,
    error: null,
    refetch: async () => { }
  });

  const fetchCrumbs = useCallback(async () => {
    if (!categoryId) {
      setState({ data: [], loading: false, error: 'ID de categoría no proporcionado', refetch: fetchCrumbs });
      return;
    }
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await ApiManager.categories.getBreadcrumbs(categoryId);

      if (response.success && response.data) {
        // Asegurar que response.data es un array
        const breadcrumbs = Array.isArray(response.data) ? response.data : [];
        setState({ data: breadcrumbs, loading: false, error: null, refetch: fetchCrumbs });
      } else {
        setState({ data: [], loading: false, error: response.error?.message || 'Error al cargar breadcrumbs', refetch: fetchCrumbs });
      }
    } catch (error) {
      setState({ data: [], loading: false, error: (error as Error).message || 'Error desconocido', refetch: fetchCrumbs });
    }
  }, [categoryId]);

  useEffect(() => { fetchCrumbs(); }, [fetchCrumbs]);
  return state;
};
